import { useState, useRef, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAudioSettings } from '../hooks/useAudioSettings';
import { useClickSound } from '../hooks/useClickSound';
import { getAuthUrl } from '../utils/api';
import CountryButton from './CountryButton';

interface MobileMenuButtonProps {
  onProfileClick: () => void;
  onAdminClick?: () => void;
  onCountryChange?: () => void;
  onLeaderboardClick: () => void;
  invitationCount?: number;
}

export default function MobileMenuButton({
  onProfileClick,
  onAdminClick,
  onCountryChange,
  onLeaderboardClick,
  invitationCount = 0,
}: MobileMenuButtonProps) {
  const { user, isAuthenticated, logout } = useAuth();
  const audioSettings = useAudioSettings();
  const { withClickSound } = useClickSound();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setShowAudioSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignIn = async () => {
    try {
      const { url } = await getAuthUrl();
      window.location.href = url;
    } catch (error) {
      console.error('Failed to get auth URL:', error);
    }
  };

  const buttonStyle: CSSProperties = {
    padding: '10px',
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  };

  const dropdownStyle: CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    background: 'rgba(10, 10, 20, 0.95)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '8px',
    minWidth: '200px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    zIndex: 200,
    maxHeight: '80vh',
    overflowY: 'auto',
  };

  const menuItemStyle: CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.9)',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    textAlign: 'left',
    transition: 'all 0.2s',
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={withClickSound(() => setShowDropdown(!showDropdown))}
        style={buttonStyle}
      >
        <svg style={{ width: '24px', height: '24px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        {invitationCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: 'linear-gradient(135deg, #14F195, #9945FF)',
              color: 'black',
              fontSize: '10px',
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: '10px',
              minWidth: '18px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(20, 241, 149, 0.4)',
            }}
          >
            {invitationCount > 9 ? '9+' : invitationCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div style={dropdownStyle}>
          {showAudioSettings ? (
            <div>
              <button
                onClick={withClickSound(() => setShowAudioSettings(false))}
                style={{
                  ...menuItemStyle,
                  marginBottom: '8px',
                  background: 'rgba(255,255,255,0.05)',
                }}
              >
                <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              <div style={{ padding: '8px 0', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', padding: '0 8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>Audio Settings</span>
                  <button
                    onClick={withClickSound(() => audioSettings.toggleMute())}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      background: audioSettings.muted ? 'rgba(239, 68, 68, 0.2)' : 'rgba(20, 241, 149, 0.2)',
                      border: `1px solid ${audioSettings.muted ? 'rgba(239, 68, 68, 0.3)' : 'rgba(20, 241, 149, 0.3)'}`,
                      color: audioSettings.muted ? '#ef4444' : '#14F195',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {audioSettings.muted ? 'Unmute' : 'Mute All'}
                  </button>
                </div>

                <div style={{ padding: '0 8px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>Sound Effects</span>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{Math.round(audioSettings.soundEffectsVolume * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={audioSettings.soundEffectsVolume}
                    onChange={(e) => audioSettings.setSoundEffectsVolume(parseFloat(e.target.value))}
                    disabled={audioSettings.muted}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      appearance: 'none',
                      cursor: audioSettings.muted ? 'not-allowed' : 'pointer',
                      opacity: audioSettings.muted ? 0.5 : 1,
                      background: `linear-gradient(to right, #14F195 0%, #14F195 ${audioSettings.soundEffectsVolume * 100}%, rgba(255,255,255,0.1) ${audioSettings.soundEffectsVolume * 100}%, rgba(255,255,255,0.1) 100%)`,
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              {isAuthenticated ? (
                <>
                  <button
                    onClick={withClickSound(() => {
                      onProfileClick();
                      setShowDropdown(false);
                    })}
                    style={menuItemStyle}
                  >
                    <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Profile
                  </button>

                  {user?.isAdmin && onAdminClick && (
                    <button
                      onClick={withClickSound(() => {
                        onAdminClick();
                        setShowDropdown(false);
                      })}
                      style={menuItemStyle}
                    >
                      <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                      </svg>
                      Admin Panel
                    </button>
                  )}

                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '8px 0' }} />

                  <button
                    onClick={withClickSound(() => {
                      logout();
                      setShowDropdown(false);
                    })}
                    style={{
                      ...menuItemStyle,
                      color: 'rgba(239, 68, 68, 0.9)',
                    }}
                  >
                    <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                    </svg>
                    Sign Out
                  </button>
                </>
              ) : (
                <button
                  onClick={withClickSound(() => {
                    handleSignIn();
                    setShowDropdown(false);
                  })}
                  style={{
                    ...menuItemStyle,
                    background: 'linear-gradient(135deg, rgba(20, 241, 149, 0.2), rgba(153, 69, 255, 0.2))',
                    border: '1px solid rgba(20, 241, 149, 0.3)',
                  }}
                >
                  <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  Sign in with X
                </button>
              )}

              <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '8px 0' }} />

              <button
                onClick={withClickSound(() => {
                  setShowAudioSettings(true);
                })}
                style={menuItemStyle}
              >
                {audioSettings.muted ? (
                  <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : (
                  <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
                Audio Settings
              </button>

              <button
                onClick={withClickSound(() => {
                  onLeaderboardClick();
                  setShowDropdown(false);
                })}
                style={menuItemStyle}
              >
                <span style={{ fontSize: '18px' }}>🏆</span>
                Leaderboard
              </button>

              {onCountryChange && (
                <>
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '8px 0' }} />
                  <div style={{ padding: '0 8px' }}>
                    <CountryButton onCountryChange={onCountryChange} />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
